// Public API: resolveIdentifier(buffer, node) → IdentifierBinding | null.
// Pure function. Given an IdentifierRef from the normalized model, return a
// structured binding pointing at its definition:
//   - 'alias'            → AliasBinding found by scope walk
//   - 'cte'              → CteBinding found by scope walk
//   - 'column'           → ReachableTable + column, resolved through snapshot
//   - 'table' | 'view'   → CTE-first, reachable-tables, snapshot fallback
//   - 'routine'          → QualifiedName + optional signature from snapshot
//   - 'variable'         → delegated to the procedural-body resolver
//   - 'parameter'        → delegated to the procedural-body resolver
// Returns null for 'schema' / 'database' / 'unknown' and for unresolvable refs.
//
// PRD: Ralph/SemanticResolverReadOnly/PRD.md § Slice: Identifier Resolver.

import type { IdentifierRef, QualifiedName } from '../../sql-grammar/model';
import type {
  IdentifierBinding,
  ParsedBuffer,
  ReachableTable,
  SchemaSnapshot,
  Scope,
  SqlPosition,
} from '../types';
import { resolveScope } from '../scope/resolveScope';
import { lookupRelationTable } from '../service/snapshotRelations';
import {
  findColumnInRelation,
  resolveRelationLabel,
  walkAliases,
  walkCtes,
  walkReachableTables,
  walkReachableTablesScoped,
} from './identifierWalkers';

export function resolveIdentifier(
  buffer: ParsedBuffer,
  node: IdentifierRef,
): IdentifierBinding | null {
  const cursor = cursorFromRange(node);
  // Variable / parameter identifiers are procedural-body locals (T-SQL @var,
  // PL/SQL / PL/pgSQL / MySQL-routine params + declares). The read-only
  // resolver does not bind them to a definition — that lives in the per-engine
  // procedural-locals side path (intellisense/resolvers/*ProceduralLocals.ts),
  // which drives completion rather than identifier binding. The former
  // ProceduralAttachment registry delegation here was dead: every producer
  // passed empty attachments, so it always returned null (plan16 T10).
  if (node.kind === 'variable' || node.kind === 'parameter') {
    return null;
  }

  const snap = buffer.schemaSnapshot;
  // Scope resolution can itself trip a stub procedural resolver on bodies
  // with no real registration. Catch the stub's throw so non-procedural
  // identifier kinds always return null rather than bubbling the error.
  let scope: Scope;
  try {
    scope = resolveScope(buffer, cursor);
  } catch {
    return null;
  }

  switch (node.kind) {
    case 'alias': return resolveAliasKind(scope, node.qualified);
    case 'cte': return resolveCteKind(scope, node.qualified);
    case 'column': return resolveColumnKind(scope, snap, node.qualified);
    case 'table': return resolveRelationKind(scope, snap, node.qualified, 'table');
    case 'view': return resolveRelationKind(scope, snap, node.qualified, 'view');
    case 'routine': return resolveRoutineKind(snap, node.qualified);
    case 'schema':
    case 'database':
    case 'unknown':
    default: return null;
  }
}

function cursorFromRange(node: IdentifierRef): SqlPosition {
  return {
    offset: node.range.startOffset,
    line: node.range.startLine,
    column: node.range.startColumn,
  };
}

function resolveAliasKind(scope: Scope, qn: QualifiedName): IdentifierBinding | null {
  const alias = walkAliases(scope, qn.foldedName);
  if (!alias) return null;
  return { kind: 'alias', target: alias, definedAt: alias.range };
}

function resolveCteKind(scope: Scope, qn: QualifiedName): IdentifierBinding | null {
  const cte = walkCtes(scope, qn.foldedName);
  if (!cte) return null;
  return { kind: 'cte', target: cte, definedAt: cte.bodyRange };
}

function resolveColumnKind(
  scope: Scope,
  snap: SchemaSnapshot | undefined,
  qn: QualifiedName,
): IdentifierBinding | null {
  const prefix = qn.foldedSchema;
  if (prefix) {
    const alias = walkAliases(scope, prefix);
    if (alias) {
      const relation: ReachableTable = {
        qualified: alias.target, via: 'alias', aliasRef: alias,
      };
      return buildColumnBinding(snap, relation, qn);
    }
    const rt = walkReachableTables(scope, prefix);
    if (rt) return buildColumnBinding(snap, rt, qn);
    return null;
  }
  let s: Scope | undefined = scope;
  while (s) {
    for (const rt of s.reachableTables) {
      const col = findColumnInRelation(snap, rt, qn.foldedName);
      if (col) return { kind: 'column', relation: rt, column: col };
    }
    s = s.parent;
  }
  return null;
}

function buildColumnBinding(
  snap: SchemaSnapshot | undefined,
  relation: ReachableTable,
  qn: QualifiedName,
): IdentifierBinding {
  const col = findColumnInRelation(snap, relation, qn.foldedName);
  return { kind: 'column', relation, column: col ?? qn };
}

function resolveRelationKind(
  scope: Scope,
  snap: SchemaSnapshot | undefined,
  qn: QualifiedName,
  fallbackKind: 'table' | 'view',
): IdentifierBinding | null {
  const cte = walkCtes(scope, qn.foldedName);
  if (cte) return { kind: 'cte', target: cte, definedAt: cte.bodyRange };

  const rt = walkReachableTablesScoped(scope, qn.foldedName, qn.foldedSchema);
  if (rt) {
    if (rt.via === 'alias' && rt.aliasRef) {
      return { kind: 'alias', target: rt.aliasRef, definedAt: rt.aliasRef.range };
    }
    return { kind: resolveRelationLabel(snap, rt.qualified, fallbackKind), target: rt.qualified };
  }

  if (snap) {
    const t = lookupRelationTable(snap, qn);
    if (t) return { kind: t.kind === 'view' ? 'view' : 'table', target: t.qualified };
  }
  return null;
}

function resolveRoutineKind(
  snap: SchemaSnapshot | undefined,
  qn: QualifiedName,
): IdentifierBinding {
  if (!snap) return { kind: 'routine', target: qn };
  const routines = snap.routines.get(qn.foldedName);
  if (!routines || routines.length === 0) return { kind: 'routine', target: qn };
  return { kind: 'routine', target: qn, signature: routines[0].signature };
}
